import { authService } from "./authService";
import config from "../config";

const API_URL = config.API_BASE_URL;

export const apiService = {
    getHeaders: (isFormData = false) => {
        const headers = { 'Accept': 'application/json' };
        if (!isFormData) headers['Content-Type'] = 'application/json';
        const token = authService.getToken();
        if (token) headers['Authorization'] = `Bearer ${token}`;
        return headers;
    },

    // Endpoints that make up the auth handshake itself. AuthContext catches their
    // 401 to try a token refresh, so sending them to /login would pre-empt that.
    authHandshakeEndpoints: ['/user', '/refresh', '/login', '/logout'],

    // A 401 anywhere else means the session is gone. Without this, the rejection
    // travels up through whichever screen happened to be polling and surfaces as
    // an uncaught runtime error instead of returning the user to the login page.
    handleUnauthorized: (endpoint) => {
        if (apiService.authHandshakeEndpoints.includes(endpoint)) return;
        if (apiService.isRedirectingToLogin) return;
        apiService.isRedirectingToLogin = true;
        authService.removeToken();
        if (window.location.pathname !== '/login') {
            window.location.href = '/login';
        }
    },

    request: async (endpoint, options = {}) => {
        const {
            durable = false,
            skipOfflineQueue = false,
            ...requestOptions
        } = options;
        const url = `${API_URL}${endpoint}`;
        const isFormData = requestOptions.body instanceof FormData;
        const headers = apiService.getHeaders(isFormData);
        const config = { ...requestOptions, headers: { ...headers, ...requestOptions.headers } };

        if (config.body && !isFormData && typeof config.body === 'object') {
            config.body = JSON.stringify(config.body);
        }

        try {
            const response = await fetch(url, config);
            if (response.status === 401) {
                apiService.handleUnauthorized(endpoint);
                throw new Error('Unauthorized');
            }
            const data = await response.json();
            if (!response.ok) throw new Error(data.message || 'Request failed');
            return data;
        } catch (error) {
            if (!skipOfflineQueue && (!navigator.onLine || durable)) {
                try {
                    const queue = JSON.parse(localStorage.getItem('offline_api_queue') || '[]');
                    queue.push({
                        endpoint,
                        options: { ...requestOptions, durable },
                    });
                    localStorage.setItem('offline_api_queue', JSON.stringify(queue));
                } catch (e) {
                    console.error('Error saving to offline queue:', e);
                }
                return { success: false, queued: true, offline: !navigator.onLine };
            }
            throw error;
        }
    },

    processOfflineQueue: async () => {
        if (!navigator.onLine) return;
        const queue = JSON.parse(localStorage.getItem('offline_api_queue') || '[]');
        if (queue.length === 0) return;

        const remainingQueue = [];
        for (const item of queue) {
            try {
                await apiService.request(item.endpoint, {
                    ...item.options,
                    skipOfflineQueue: true,
                });
            } catch (error) {
                remainingQueue.push(item);
            }
        }
        localStorage.setItem('offline_api_queue', JSON.stringify(remainingQueue));
    },

    get: (endpoint) => apiService.request(endpoint, { method: 'GET' }),
    post: (endpoint, data) => apiService.request(endpoint, { method: 'POST', body: JSON.stringify(data) }),
    postDurable: (endpoint, data) => apiService.request(endpoint, {
        method: 'POST',
        body: JSON.stringify(data),
        durable: true,
    }),
    postFormData: (endpoint, formData) => apiService.request(endpoint, { method: 'POST', body: formData }),
    put: (endpoint, data) => apiService.request(endpoint, { method: 'PUT', body: JSON.stringify(data) }),
    delete: (endpoint) => apiService.request(endpoint, { method: 'DELETE' }),
};
